import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { Control, UseFormSetValue } from 'react-hook-form'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Title from '@/components/Title'
import EditInputField from '@/pages/getRecipe/allInOne/panel/components/EditInputField'

import Button from './Button'
import SnackbarTooltip from './SnackbarTooltip'

interface ColorSelectionProps {
  name: string
  control: Control<any>
  error?: boolean
  title: string
  clearErrors: any
  setValue: UseFormSetValue<any>
  data: any
  selectedPickerKey: string
  onclick: any
  containerRef?: React.RefObject<HTMLDivElement>
  snackbarClose?: boolean
  hasRecipe?: boolean
  regenerateRecipe?: (region: string, currentColorData: any) => void
  disableButton?: boolean
}

const ColorSelection = ({
  name,
  control,
  error = false,
  title,
  clearErrors,
  setValue,
  data,
  selectedPickerKey,
  onclick,
  containerRef,
  snackbarClose = false,
  hasRecipe = false,
  regenerateRecipe,
  disableButton,
}: ColorSelectionProps) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const shouldShowError = useMemo(() => {
    return error && Array.isArray(data)
      ? data?.filter(Boolean)?.length === 0
      : !data
  }, [error, data])

  useEffect(() => {
    if (!shouldShowError) {
      clearErrors(name)
    }
  }, [shouldShowError])

  useEffect(() => {
    if (snackbarClose) {
      setSnackbarOpen(false)
    }
  }, [snackbarClose])

  return (
    <Box>
      <Box display="flex" alignItems="center" gap="10px" mb={1}>
        <Title fontSize="16px" lineHeight="normal" maxWidth={120}>
          {title}
        </Title>
        <Button
          variant="outlined"
          size="small"
          label={
            selectedPickerKey === name ? (
              <CheckIcon fontSize="small" />
            ) : (
              <img src={eyedrop} alt="Select" width={16} height={16} />
            )
          }
          sx={{
            alignSelf: 'flex-start',
            fontSize: '16px',
            padding: '4px 10px',
            cursor: 'pointer',
            lineHeight: 'normal',
            height: '30px',
            width: '36px',
            minWidth: '36px',
            borderRadius: '4px',
          }}
          textTransform="uppercase"
          onClick={() => {
            onclick()
            setSnackbarOpen(true)
          }}
        />
        {hasRecipe && (
          <Button
            variant="outlined"
            size="small"
            label={<RefreshIcon fontSize="small" />}
            textTransform="none"
            sx={{
              alignSelf: 'flex-start',
              fontSize: '16px',
              padding: '4px 10px',
              cursor: disableButton ? 'not-allowed' : 'pointer',
              opacity: disableButton ? 0.5 : 1,
              lineHeight: 'normal',
              height: '30px',
              width: '36px',
              minWidth: '36px',
              borderRadius: '4px',
            }}
            onClick={() => {
              regenerateRecipe?.(name, data)
            }}
            disabled={disableButton}
            tooltip={'Re-generate Recipe'}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {[0, 1, 2].map((index) => (
          <EditInputField
            key={index}
            name={name}
            index={index}
            data={data}
            setValue={setValue}
            shouldShowError={shouldShowError}
            control={control}
          />
        ))}
      </Box>

      <SnackbarTooltip
        containerRef={containerRef}
        message="Click on the spot you want to measure."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -100]}
        maxWidth={500}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default ColorSelection
